import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { PrModalComponent } from '@app/components/pr-modal/pr-modal/pr-modal.component';
import { UserDao } from '@app/dao/user/user.dao';
import { EaSimulationPacksComponent } from '@app/dashboard/sheets/shared/ea-simulation-packs/ea-simulation-packs.component';
import {
  SimulationPack,
  SimulationPackService,
} from '@app/dashboard/sheets/shared/ea-simulation-packs/simulation-pack.service';
import { InfoOption } from '@app/forms/subscription/components/info/info.component';
import { CommandEnum } from '@app/forms/subscription/definition/interface/enum';
import { infoOption } from '@app/forms/subscription/services/ref';
import { SubscriptionFacadeService } from '@app/forms/subscription/services/subscription-facade.service';
import { leavePackStepWithoutPack } from '@app/forms/subscription/ea-subscription-forms/pledge-pack-choice';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize, take } from 'rxjs';
import {
  allocateAmounts,
  FULL_PERCENT_TOLERANCE,
  roundTo,
  toNumber,
} from '../services/versement-allocation';
import {
  VersementPackResolvedLine,
  VersementPackSupportsService,
} from '../services/versement-pack-supports.service';

@UntilDestroy()
@Component({
  selector: 'versement-simulation-packs',
  templateUrl: './versement-simulation-packs.component.html',
  styleUrls: ['./versement-simulation-packs.component.scss'],
  standalone: false,
})
export class VersementSimulationPacksComponent
  implements OnInit, AfterViewInit
{
  optionInfo1: InfoOption = infoOption;
  packService: string = 'life_insurance';
  score = undefined;
  loading: boolean = true;
  resolving: boolean = false;
  resolutionError: string = null;
  resolvedLines: VersementPackResolvedLine[] = [];

  @ViewChild(EaSimulationPacksComponent, { read: SimulationPackService })
  simulationPackService: SimulationPackService;

  @ViewChild('modalPackChange', { static: true, read: PrModalComponent })
  modalPackChange: PrModalComponent;

  constructor(
    private userDao: UserDao,
    private cd: ChangeDetectorRef,
    private subscriptionFacade: SubscriptionFacadeService,
    private packSupportsService: VersementPackSupportsService,
  ) {}

  ngOnInit() {
    const pledge = this.subscriptionFacade.getPledge();
    this.packService = pledge.productType === 'PER' ? 'per' : 'life_insurance';

    const owners = pledge.owners;
    this.userDao
      .getAuthentified()
      .pipe(take(1))
      .subscribe(user => {
        const { score } =
          owners?.find(owner => owner.id === user.id) || owners?.[0] || {};

        this.score = score;
      });
  }

  ngAfterViewInit() {
    this.cd.detectChanges();
    this.simulationPackService.productId = this.productId;
    this.simulationPackService
      .getPacks()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: packs => {
          this.loading = false;
          this.restoreSelectedPack(packs);
          this.cd.detectChanges();
          leavePackStepWithoutPack(packs, this.subscriptionFacade);
        },
        error: () => {
          this.loading = false;
          this.cd.detectChanges();
        },
      });
  }

  restoreSelectedPack(packs: SimulationPack[]) {
    if (this.simulationPackService.selectedPack) {
      return;
    }
    const packId = this.pledgeParameters?.productPackId;
    const pack = packs?.find(p => p.id === (packId as any));
    if (pack) {
      this.simulationPackService.selectPack(pack);
      this.onPackSelected(pack);
    }
  }

  onPackSelected(pack: SimulationPack) {
    this.resolutionError = null;
    this.resolvedLines = [];

    if (!pack?.id) {
      return;
    }

    this.resolving = true;
    this.packSupportsService
      .resolve(pack.allocation || [], this.productId)
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          this.resolving = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: ({ lines, unresolvedIsins }) => {
          if (unresolvedIsins.length) {
            this.resolutionError = `Certains supports de cette allocation ne sont pas disponibles sur ce contrat : ${unresolvedIsins.join(
              ', ',
            )}.`;
            return;
          }
          this.resolvedLines = lines;
        },
        error: () => {
          this.resolutionError =
            "Impossible de charger la composition de l'allocation sélectionnée.";
        },
      });
  }

  get productId(): number {
    return this.subscriptionFacade.getPledge()?.products?.[0]?.productId;
  }

  get selectedPack(): SimulationPack {
    return this.simulationPackService?.selectedPack;
  }

  get totalPercentage(): number {
    return this.resolvedLines.reduce(
      (acc, line) => acc + (Number(line.allocationPercentage) || 0),
      0,
    );
  }

  get isPackComplete(): boolean {
    return (
      !!this.selectedPack?.id &&
      !this.resolving &&
      !this.resolutionError &&
      this.resolvedLines.length > 0 &&
      Math.abs(this.totalPercentage - 1) < FULL_PERCENT_TOLERANCE
    );
  }

  get versementAmount(): number {
    const pledge = this.subscriptionFacade.getPledge();
    if (
      !['VERSEMENT_COMPLEMENTAIRE', 'VERSEMENT_PROGRAMME'].includes(
        pledge?.operation,
      )
    ) {
      return 0;
    }

    return toNumber(
      pledge?.products?.[0]?.parameters?.supportsInvestment?.totalAmount,
    );
  }

  extract() {
    const percents = this.resolvedLines.map(
      line => toNumber(line.allocationPercentage) * 100,
    );
    const amounts = allocateAmounts(percents, this.versementAmount);

    const supportRepartition = this.resolvedLines.map((line, index) => ({
      supportId: line.id,
      supportName: line.name,
      supportType: line.type,
      isin: line.isin,
      isr: line.isr,
      amount: amounts[index] || 0,
      percentage: toNumber(line.allocationPercentage),
      index,
      perfP3Y: line.details?.performances?.perfP3Y,
      volat3Y: line.details?.performances?.volat3Y,
      risk: line.details?.profilRisk?.value,
    }));

    return [
      {
        ownerId: null,
        productId: this.productId,
        parameters: {
          productPackId: this.selectedPack.id,
          supportsInvestment: {
            totalAmount: roundTo(
              this.versementAmount > 0
                ? this.versementAmount
                : supportRepartition.reduce(
                    (sum, line) => sum + line.amount,
                    0,
                  ),
              2,
            ),
            supportRepartition,
          },
        },
      },
    ];
  }

  private get pledgeParameters(): any {
    return this.subscriptionFacade.getPledge()?.products?.[0]?.parameters || {};
  }

  private hasWorkInProgress(parameters: any): boolean {
    const lines = parameters.supportsInvestment?.supportRepartition || [];
    if (!lines.length) {
      return false;
    }

    const previousPack = (this.simulationPackService?.packs || []).find(
      p => p.id === (parameters.productPackId as any),
    );
    if (!previousPack) {
      return true;
    }

    const previousAllocation = (previousPack.allocation || []) as any[];
    const packIsins = previousAllocation.map((item: any) => item.supportIsin);

    if (
      lines.length !== packIsins.length ||
      lines.some(line => !packIsins.includes(line.isin))
    ) {
      return true;
    }

    return lines.some(line => {
      const packLine = previousAllocation.find(
        (item: any) => item.supportIsin === line.isin,
      );
      const persistedPercent = toNumber(line.percentage) * 100;
      const packPercent = toNumber(packLine?.allocationPercentage) * 100;
      return Math.abs(persistedPercent - packPercent) >= FULL_PERCENT_TOLERANCE;
    });
  }

  onSubmit() {
    if (!this.isPackComplete) {
      return;
    }

    const parameters = this.pledgeParameters;
    const samePackSelected =
      parameters.productPackId != null &&
      this.selectedPack.id === (parameters.productPackId as any);
    const hasLines = !!(parameters.supportsInvestment?.supportRepartition || [])
      .length;

    if (samePackSelected && hasLines) {
      this.subscriptionFacade.nextStep();
      return;
    }

    if (
      parameters.productPackId != null &&
      this.hasWorkInProgress(parameters)
    ) {
      this.modalPackChange.open();
      return;
    }

    // PRE-4896 : préremplit les 4 champs "*Autocomplete" de la slide "Taux par support" pour les
    // supports du pack sélectionné — sans quoi ils restent null pour ce chemin de sélection,
    // contrairement à la sélection libre (cf. support-form.component.ts).
    this.subscriptionFacade.setCommand([CommandEnum.COMPLETE_SUPPORT_RATES]);
    this.subscriptionFacade.submitPledgeWithoutForm(this as any);
  }

  confirmPackChange() {
    this.modalPackChange.close();
    this.subscriptionFacade.setCommand([CommandEnum.COMPLETE_SUPPORT_RATES]);
    this.subscriptionFacade.submitPledgeWithoutForm(this as any);
  }

  cancelPackChange() {
    this.modalPackChange.close();
  }

  prev() {
    this.subscriptionFacade.previousStep();
  }
}
